import {
  SeoPages,
  SeoSettings,
  SeoSettingsPayload,
} from '../../../common/models'

export interface SeoFormItem {
  metaTitle?: string | null
  metaDescription?: string | null
  metaKeywords?: string | null
  metaOgImageKey?: string | null
  metaOgTitle?: string | null
  metaOgDescription?: string | null
  metaOgSiteName?: string | null
  metaOgType?: string | null
  metaOgUrl?: string | null
  metaOgImageSecureKey?: string | null
  metaOgImageWidth?: number | null
  metaOgImageHeight?: number | null
  metaOgImageType?: string | null
  metaOgImageAlt?: string | null
  metaOgLocale?: string | null
  metaTwitterCard?: string | null
  metaTwitterUrl?: string | null
  metaTwitterTitle?: string | null
  metaTwitterDescription?: string | null
  metaTwitterImageKey?: string | null
}

export interface ISEOValues {
  content: SeoFormItem[]
}

export type SeoImageFieldName = 'metaOgImageKey' | 'metaTwitterImageKey'

export interface SeoFieldConfig {
  kind: 'input' | 'image'
  name: keyof SeoFormItem
  label: string
  placeholder?: string
  type?: 'text' | 'number'
  fullWidth?: boolean
}

// Ordered exactly as the Figma design (flat 2-column grid, full-width rows
// for the image uploads and the secure URL).
export const SEO_FIELDS: SeoFieldConfig[] = [
  {
    kind: 'input',
    name: 'metaDescription',
    label: 'Meta Description',
    placeholder: 'Enter Meta Description',
  },
  {
    kind: 'input',
    name: 'metaKeywords',
    label: 'Meta Keywords',
    placeholder: 'Enter Meta Keywords',
  },
  {
    kind: 'input',
    name: 'metaTitle',
    label: 'Meta Title',
    placeholder: 'Enter Meta Title',
  },
  {
    kind: 'input',
    name: 'metaOgTitle',
    label: 'Meta OG Title',
    placeholder: 'Enter Meta OG Title',
  },
  {
    kind: 'input',
    name: 'metaOgDescription',
    label: 'Meta OG Description',
    placeholder: 'Enter Meta OG Description',
  },
  {
    kind: 'input',
    name: 'metaOgSiteName',
    label: 'Meta OG Site Name',
    placeholder: 'Enter Meta OG Site Name',
  },
  {
    kind: 'input',
    name: 'metaOgType',
    label: 'Meta OG Type',
    placeholder: 'Enter Meta OG Type',
  },
  {
    kind: 'input',
    name: 'metaOgUrl',
    label: 'Meta OG URL',
    placeholder: 'Enter Meta OG URL',
  },
  {
    kind: 'image',
    name: 'metaOgImageKey',
    label: 'Meta OG Image (1200X600)',
  },
  {
    kind: 'input',
    name: 'metaOgImageSecureKey',
    label: 'Meta OG Image Secure URL',
    placeholder: 'Enter Meta OG Image Secure URL',
    fullWidth: true,
  },
  {
    kind: 'input',
    name: 'metaOgImageWidth',
    label: 'Meta OG Image Width',
    placeholder: 'Enter Meta OG Image Width',
    type: 'number',
  },
  {
    kind: 'input',
    name: 'metaOgImageHeight',
    label: 'Meta OG Image Height',
    placeholder: 'Enter Meta OG Image Height',
    type: 'number',
  },
  {
    kind: 'input',
    name: 'metaOgImageType',
    label: 'Meta OG Image Type',
    placeholder: 'Enter Meta OG Image Type',
  },
  {
    kind: 'input',
    name: 'metaOgImageAlt',
    label: 'Meta OG Image Alt',
    placeholder: 'Enter Meta OG Image Alt',
  },
  {
    kind: 'input',
    name: 'metaOgLocale',
    label: 'Meta OG Locale',
    placeholder: 'Enter Meta OG Locale',
  },
  {
    kind: 'input',
    name: 'metaTwitterCard',
    label: 'Meta Twitter Card',
    placeholder: 'Enter Meta Twitter Card',
  },
  {
    kind: 'input',
    name: 'metaTwitterUrl',
    label: 'Meta Twitter URL',
    placeholder: 'Enter Meta Twitter URL',
  },
  {
    kind: 'input',
    name: 'metaTwitterTitle',
    label: 'Meta Twitter Title',
    placeholder: 'Enter Meta Twitter Title',
  },
  {
    kind: 'input',
    name: 'metaTwitterDescription',
    label: 'Meta Twitter Description',
    placeholder: 'Enter Meta Twitter Description',
  },
  {
    kind: 'image',
    name: 'metaTwitterImageKey',
    label: 'Meta Twitter Image',
  },
]

export const SEO_IMAGE_FIELD_NAMES: SeoImageFieldName[] = [
  'metaOgImageKey',
  'metaTwitterImageKey',
]

// All editable keys, used to hydrate / reset the form per page.
export const SEO_ALL_FIELDS: (keyof SeoFormItem)[] = SEO_FIELDS.map(
  (f) => f.name,
)

export const SEO_PAGE_LABELS: Record<SeoPages, string> = {
  HOMEPAGE: 'Homepage',
  TICKETS: 'Tickets',
}

// GET returns the og/twitter images as full URLs (`*Link`); the form/payload
// use the upload key (`*Key`), so on load we derive the key from the URL.
export const SEO_IMAGE_LINK_FIELD: Record<
  SeoImageFieldName,
  keyof SeoSettings
> = {
  metaOgImageKey: 'metaOgImageLink',
  metaTwitterImageKey: 'metaTwitterImageLink',
}

export const fileNameFromUrl = (link?: string | null): string => {
  if (!link) return ''
  try {
    const url = new URL(link)
    return url.pathname.substring(url.pathname.lastIndexOf('/') + 1)
  } catch {
    return link
  }
}

// Map a GET response onto the key-based form values.
export const toSeoFormItem = (data: SeoSettings): SeoFormItem => ({
  metaTitle: data.metaTitle ?? null,
  metaDescription: data.metaDescription ?? null,
  metaKeywords: data.metaKeywords ?? null,
  metaOgImageKey: fileNameFromUrl(data.metaOgImageLink),
  metaOgTitle: data.metaOgTitle ?? null,
  metaOgDescription: data.metaOgDescription ?? null,
  metaOgSiteName: data.metaOgSiteName ?? null,
  metaOgType: data.metaOgType ?? null,
  metaOgUrl: data.metaOgUrl ?? null,
  metaOgImageSecureKey: data.metaOgImageSecureLink ?? null,
  metaOgImageWidth: data.metaOgImageWidth ?? null,
  metaOgImageHeight: data.metaOgImageHeight ?? null,
  metaOgImageType: data.metaOgImageType ?? null,
  metaOgImageAlt: data.metaOgImageAlt ?? null,
  metaOgLocale: data.metaOgLocale ?? null,
  metaTwitterCard: data.metaTwitterCard ?? null,
  metaTwitterUrl: data.metaTwitterUrl ?? null,
  metaTwitterTitle: data.metaTwitterTitle ?? null,
  metaTwitterDescription: data.metaTwitterDescription ?? null,
  metaTwitterImageKey: fileNameFromUrl(data.metaTwitterImageLink),
})

// Image preview state (name + url) for a page's two upload fields.
export const toSeoImageStates = (
  index: number,
  data: SeoSettings,
): Record<string, { name: string; url: string }> =>
  SEO_IMAGE_FIELD_NAMES.reduce((acc, field) => {
    const link = data[SEO_IMAGE_LINK_FIELD[field]] as string | null
    acc[`${index}.${field}`] = {
      name: fileNameFromUrl(link),
      url: link || '',
    }
    return acc
  }, {} as Record<string, { name: string; url: string }>)

// Build the PUT payload from a single page's form values.
export const toSeoPayload = (item: SeoFormItem): SeoSettingsPayload => ({
  metaTitle: item.metaTitle || null,
  metaDescription: item.metaDescription || null,
  metaKeywords: item.metaKeywords || null,
  metaOgImageKey: item.metaOgImageKey || null,
  metaOgTitle: item.metaOgTitle || null,
  metaOgDescription: item.metaOgDescription || null,
  metaOgSiteName: item.metaOgSiteName || null,
  metaOgType: item.metaOgType || null,
  metaOgUrl: item.metaOgUrl || null,
  metaOgImageSecureKey: item.metaOgImageSecureKey || null,
  metaOgImageWidth: item.metaOgImageWidth || null,
  metaOgImageHeight: item.metaOgImageHeight || null,
  metaOgImageType: item.metaOgImageType || null,
  metaOgImageAlt: item.metaOgImageAlt || null,
  metaOgLocale: item.metaOgLocale || null,
  metaTwitterCard: item.metaTwitterCard || null,
  metaTwitterUrl: item.metaTwitterUrl || null,
  metaTwitterTitle: item.metaTwitterTitle || null,
  metaTwitterDescription: item.metaTwitterDescription || null,
  metaTwitterImageKey: item.metaTwitterImageKey || null,
})

export type SeoSettingsResponse = SeoSettings
